import React, { useMemo, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Globe,
  Info,
  Lightbulb,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { toast } from 'sonner';
import {
  DnsRecordsTable,
  PLATFORM_PRIMARY_BUTTON,
  PLATFORM_SETTINGS_CARD,
  PLATFORM_SETTINGS_INPUT,
  validateDomainInput,
  generateMailgunDnsRecords,
  evaluateVerificationResponse,
} from './EmailDomainComponents';

const SETUP_BULLETS = (domain) => [
  `Your domain [${domain}] will be connected to the platform email provider.`,
  'DNS records will be shown below with copy buttons.',
  'Update DNS records with your DNS provider (this is not done inside FundElite).',
  'Return here and click Verify Records after DNS changes propagate.',
  'Existing Mailgun domain logs may not appear in the FundElite dashboard until verification completes.',
];

const PlatformEmailDomainSetupFlow = ({
  initialDomain = '',
  domainStatus,
  providerDnsRecords = [],
  onBack,
  onSaveDomain,
  onVerifyDomain,
  saving = false,
  verifying = false,
}) => {
  const [domainInput, setDomainInput] = useState(initialDomain);
  const [step, setStep] = useState('add');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState(null);
  const [recordVerificationStatuses, setRecordVerificationStatuses] = useState(null);

  const validation = useMemo(() => validateDomainInput(domainInput), [domainInput]);
  const activeDomain = validation.domain;
  const dnsRecords = useMemo(
    () =>
      generateMailgunDnsRecords(activeDomain, {
        providerRecords: providerDnsRecords,
      }).map((record) => ({
        ...record,
        status:
          recordVerificationStatuses?.[record.id] ||
          (record.id === 'tracking'
            ? recordVerificationStatuses?.tracking_cname
            : null) ||
          record.status,
      })),
    [activeDomain, providerDnsRecords, recordVerificationStatuses]
  );

  const handleDomainChange = (e) => {
    setDomainInput(e.target.value.toLowerCase());
  };

  const handleAddAndVerify = () => {
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmContinue = async () => {
    setConfirmOpen(false);
    try {
      if (onSaveDomain) {
        await onSaveDomain(activeDomain);
      }
      setStep('dns');
      toast.success('Domain saved. Add the DNS records below, then verify.');
    } catch (err) {
      toast.error(err?.message || 'Failed to save domain');
    }
  };

  const handleAddRecordsManually = () => {
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }
    setConfirmOpen(false);
    setStep('dns');
  };

  const handleVerifyRecords = async () => {
    setVerificationMessage(null);
    try {
      const result = await onVerifyDomain?.();
      const evaluation = result?.evaluation || evaluateVerificationResponse(result);
      setRecordVerificationStatuses(evaluation.recordStatuses);

      if (evaluation.level === 'success') {
        setVerificationMessage({
          type: 'success',
          text: evaluation.message,
        });
      } else if (evaluation.level === 'error') {
        setVerificationMessage({
          type: 'error',
          text: evaluation.message,
        });
      } else {
        setVerificationMessage({
          type: evaluation.level === 'info' ? 'info' : 'warning',
          text: evaluation.message,
        });
      }
    } catch (err) {
      setVerificationMessage({
        type: 'error',
        text: err?.message || 'Verification failed. Review the DNS records and try again.',
      });
    }
  };

  if (step === 'dns') {
    return (
      <div className="space-y-6" data-testid="platform-domain-dns-setup">
        <button
          type="button"
          onClick={() => setStep('add')}
          className="flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to domain entry
        </button>

        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 rounded-xl">
            <Globe className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">DNS Records</h2>
            <p className="text-sm text-zinc-400">
              Set up <span className="text-amber-400 font-mono">{activeDomain}</span> manually
            </p>
          </div>
        </div>

        <Card className={PLATFORM_SETTINGS_CARD}>
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg">Set up your domain manually</CardTitle>
            <CardDescription className="text-zinc-400">
              Add the following records in your DNS provider, then return here and click Verify Records.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <DnsRecordsTable
              records={dnsRecords}
              domainStatus={domainStatus}
              verificationStatuses={recordVerificationStatuses}
            />

            {verificationMessage && (
              <div
                className={`flex items-start gap-3 p-4 rounded-xl border text-sm ${
                  verificationMessage.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                    : verificationMessage.type === 'error'
                      ? 'bg-red-500/10 border-red-500/30 text-red-300'
                      : verificationMessage.type === 'info'
                        ? 'bg-blue-500/10 border-blue-500/30 text-blue-200'
                        : 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                }`}
                data-testid="dns-verification-message"
              >
                {verificationMessage.type === 'success' ? (
                  <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
                ) : verificationMessage.type === 'info' ? (
                  <Info className="w-5 h-5 shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                )}
                <p>{verificationMessage.text}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <Button
                variant="outline"
                onClick={onBack}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Back to Domains
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('add')}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Back
                </Button>
                <Button
                  onClick={handleVerifyRecords}
                  disabled={verifying}
                  className={PLATFORM_PRIMARY_BUTTON}
                  data-testid="verify-dns-records-btn"
                >
                  {verifying ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Verify Records
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-domain-add-setup">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 transition-colors"
        data-testid="back-to-domains-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Domains
      </button>

      <div>
        <h2 className="text-2xl font-bold text-white">Add new domain</h2>
        <p className="text-sm text-zinc-400 mt-1">
          Sending from a dedicated domain improves the likelihood of landing in the inbox.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className={PLATFORM_SETTINGS_CARD}>
            <CardContent className="pt-6 space-y-5">
              <div className="space-y-2">
                <Label className="text-zinc-400">Enter Domain Name</Label>
                <Input
                  placeholder="mg.fundelite.com"
                  value={domainInput}
                  onChange={handleDomainChange}
                  className={PLATFORM_SETTINGS_INPUT}
                  data-testid="add-domain-input"
                />
                {validation.valid && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-300">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    Looks good! Click &quot;Add &amp; Verify&quot; to proceed.
                  </div>
                )}
                {domainInput && !validation.valid && (
                  <p className="text-xs text-red-400">{validation.error}</p>
                )}
                <p className="text-xs text-zinc-500">Use only lowercase letters in the domain name.</p>
              </div>

              <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700 space-y-2">
                <ul className="space-y-2 text-sm text-zinc-400 list-disc list-inside">
                  {validation.valid
                    ? SETUP_BULLETS(activeDomain).map((item) => <li key={item}>{item}</li>)
                    : SETUP_BULLETS('your-domain.com').map((item) => <li key={item}>{item}</li>)}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={onBack}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Previous
                </Button>
                <Button
                  onClick={handleAddAndVerify}
                  disabled={!validation.valid || saving}
                  className={PLATFORM_PRIMARY_BUTTON}
                  data-testid="add-and-verify-btn"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Add &amp; Verify
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className={PLATFORM_SETTINGS_CARD}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-400" />
                <CardTitle className="text-white text-base">Use a dedicated sending subdomain</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-zinc-400 leading-relaxed">
                We recommend using a subdomain for Mailgun (for example,{' '}
                <span className="text-amber-400 font-mono">mg.yourdomain.com</span>) so sending
                reputation is isolated from your root domain.
              </p>
            </CardContent>
          </Card>

          <Card className={PLATFORM_SETTINGS_CARD}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-amber-400" />
                <CardTitle className="text-white text-base">Email warming process</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Inbox providers build sending reputation over the first 2–4 weeks. Start with low
                volume and increase gradually for best deliverability.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          className="bg-zinc-900 border-zinc-700 text-white max-w-md sm:max-w-lg"
          data-testid="connect-domain-modal"
        >
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <Globe className="w-5 h-5 text-emerald-400" />
              </div>
              <DialogTitle className="text-white">Connect your domain/subdomain</DialogTitle>
            </div>
            <DialogDescription className="text-zinc-400">
              Confirm the sending domain to connect with the platform email provider.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label className="text-zinc-400">Domain URL *</Label>
            <Input
              value={activeDomain}
              readOnly
              className={`${PLATFORM_SETTINGS_INPUT} font-mono`}
            />
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={handleAddRecordsManually}
              className="text-amber-400 hover:text-amber-300 hover:bg-zinc-800 p-0 h-auto"
              data-testid="add-record-manually-btn"
            >
              Add record manually
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmOpen(false)}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmContinue}
                disabled={saving}
                className={PLATFORM_PRIMARY_BUTTON}
                data-testid="connect-domain-continue-btn"
              >
                Continue
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformEmailDomainSetupFlow;
